import type { Epic } from 'redux-observable';
import type { Observable } from 'rxjs/Observable';
import { concat } from 'rxjs/observable/concat';
import { of } from 'rxjs/observable/of';
import { timer } from 'rxjs/observable/timer';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/concatMap';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/exhaustMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/takeWhile';
import 'rxjs/add/operator/timeout';
import log from '@atlassian/jira-common-util-logging/src/log';
import type { VelocityCalculationTaskStatus } from '../../model/types';
import {
	startVelocityCalculationTask,
	getVelocityCalculationTaskStatus,
} from '../../services/velocity-data-fetch';
import {
	VELOCITY_DATA_FETCH,
	velocityDataFetchInProgress,
	velocityDataFetchSuccess,
	velocityDataFetchFailure,
} from '../../state/actions/velocity-data-fetch';
import type { Action, State } from '../../state/types';

export const POLLING_INTERVAL_MS = 1250;
export const POLLING_NO_PROGRESS_TIMEOUT_MS = 30000;

/**
 * Starts a Velocity calculation task and performs its result polling.
 *
 * The error is emitted if there is no progress on Velocity calculation after the certain number of attempts.
 *
 * Polling is handled with a fixed time interval, and the implementation ensures that there are no concurrent (overlapping)
 * polling requests regardless of latency.
 */
export const velocityDataFetchEpic: Epic<Action, State> = (action$, store) =>
	action$
		.ofType(VELOCITY_DATA_FETCH)
		.switchMap(() =>
			startVelocityCalculationTask(store).mergeMap((task) =>
				timer(0, POLLING_INTERVAL_MS)
					.exhaustMap(() => getVelocityCalculationTaskStatus(store, task.taskId)) // Do not send new polling requests while there is an ongoing one.
					// The usage of `concatMap`/`takeWhile` below represents is a workaround for RxJS 5,
					// which does not provide an inclusive `takeWhile` operator.
					// https://github.com/ReactiveX/rxjs/issues/4000#issuecomment-419192076
					.concatMap((taskStatus) => (taskStatus.isRunning ? of(taskStatus) : of(taskStatus, null)))
					.takeWhile((taskStatus) => !!taskStatus)
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					.map((taskStatus) => taskStatus!)
					.distinctUntilChanged(
						(previous, current) =>
							previous.isRunning === current.isRunning && previous.progress === current.progress,
					)
					.timeout(POLLING_NO_PROGRESS_TIMEOUT_MS)
					.mergeMap(mapTaskStatusToActions),
			),
		)
		.catch((error) => {
			log.safeErrorWithoutCustomerData(
				'software-reports.velocity-report.failure',
				'Failed to fetch Velocity data.',
				error,
			);
			return of(velocityDataFetchFailure({ error }));
		});

const mapTaskStatusToActions = (taskStatus: VelocityCalculationTaskStatus): Observable<Action> => {
	if (taskStatus.isRunning) {
		return of(
			velocityDataFetchInProgress({
				progress: taskStatus.progress,
				progressUpdate: taskStatus.progressUpdate,
			}),
		);
	}

	// In order to make progress reporting smoother on UI, emit an action representing 100% progress and
	// then an action representing the task completion.
	return concat(
		of(
			velocityDataFetchInProgress({
				progress: taskStatus.progress,
				progressUpdate: taskStatus.progressUpdate,
			}),
		),
		timer(250).map(() => velocityDataFetchSuccess({ sprints: taskStatus.result ?? [] })),
	);
};
